/**
 * `rankerGroupOrder` — QA batch 3 unit G5 (plan
 * `plans/signals-tab-pr1/qa-batch3-g5-ranker-group-order-plan.md` D-A…D-F).
 * Type and Step-type groups list in Action View's order, not the server's
 * alphabetical `ORDER BY <column> ASC NULLS LAST`. The rule is Action View's
 * `groupByLevels` comparator (mini-apps/action-view/grouping.ts:282-383),
 * copied as a sort key per group (never imported — coding-standards §1):
 *
 * | level key                          | group sort key (first component wins)                          | auto-loads every page |
 * |------------------------------------|------------------------------------------------------------------|---|
 * | `signal_source_type` (anywhere)    | min member priority, Ranker `typeMinRank`, larger first, server order | yes |
 * | `signal_tactic` under a Type level | min member priority, tier, `stepTypeRank`, server order        | no |
 * | `signal_tactic`, no Type ancestor  | tier, `stepTypeRank`, server order (priority NOT consulted — AV `:220-223`) | no |
 * | other server key under a Type level| min member priority, server order                              | no |
 * | anything else                      | not overridden — `arrangeSignalsClientSide`'s default          | no (unless a client level) |
 *
 * "Server order" (the first row index of the group) is the final tiebreak
 * everywhere, so the order is total and deterministic. Deliberate
 * deviations from AV (plan §5): an inherited-level tie falls to server
 * order, not size; Local time / Business hours under a Type parent keep
 * their fixed clock order (never overridden here).
 *
 * A group at level `i` is the distinct tuple of group VALUES over
 * `groupBy[0..i]` — every level, client ones included — i.e. exactly the
 * header path FlowTable renders the row under, so a rank is computed per
 * visible group (Type under an hour bucket is ranked within that bucket).
 * Tuple identity, not adjacency: two server runs that read the same label
 * (`''` sorts first and `NULL` last; both read `No Type`) are one group, as
 * in AV (its groups are a Map by label). G5 D-D introduced this for the
 * overridden levels only; fix-plan-batch3.md D-C (I1) made it the arranger's
 * rule at EVERY level, and both modules now build the tuple with the same
 * exported `groupValueOf` / `GROUP_PATH_SEP`, so the identities cannot drift.
 *
 * Reading 1..n down the default `signal-my` tree follows from
 * `prioritizeSignals`' type/step contiguity (fix-plan-batch3.md D-D), not
 * from this module's tiebreaks: a min-priority group order is 1..n only
 * when every group's numbers are one contiguous stretch.
 *
 * Ranks are group positions in one global sort; the arranger compares a
 * level only among rows that tie on every earlier level, so the ranks need
 * only be right within a parent (its `ServerLevelRankFn` contract).
 */
import { isClientGroupLevel, type ServerLevelRankFn } from './arrangeSignalsClientSide';
import { groupValueOf, GROUP_PATH_SEP } from '../table-core/grouping';
import type { CanonicalRanking } from './rankingFallback';
import { stepTypeRank } from './stepTypeRank';

/** The fields the rule reads — a structural in-memory row shape (like `ClientSortableSignalRow`), not a schema. */
export interface RankerOrderableRow {
  priority?: number | null;
  signal_source_type?: string | null;
  signal_tactic?: string | null;
  [key: string]: unknown;
}

const TYPE_KEY = 'signal_source_type';
const STEP_KEY = 'signal_tactic';

/** Group levels whose ORDER comes from the Ranker / the canonical step order — each needs every page loaded (D-B). */
export const RANKER_ORDERED_GROUP_KEYS: readonly string[] = [TYPE_KEY, STEP_KEY];

export function isRankerOrderedLevel(key: string): boolean {
  return RANKER_ORDERED_GROUP_KEYS.includes(key);
}

/** Levels whose group ORDER needs every page before it is right: Type is ordered by the Ranker across the
 *  whole list (G5 §1.5 — the server pages types alphabetically, so the Ranker's #1 type can sit pages deep).
 *  Step (`signal_tactic`) is Ranker-ordered too but by a fixed label vocabulary, so it orders correctly over
 *  whatever is loaded; it is deliberately NOT here (qa-batch4-plan.md D-C, reversing G5 R2): landing on All
 *  Signals (Assignee → Step) shows the first page and Load more, and a later page may insert `Manual email`
 *  between `Call` and `LinkedIn connect` inside the last loaded rep — accepted. */
export const EVERY_PAGE_GROUP_KEYS: readonly string[] = [TYPE_KEY];

/** True when the active grouping needs the whole list — a client-computed level (Local time / Business
 *  hours, `isClientGroupLevel`) or a level in `EVERY_PAGE_GROUP_KEYS`. Drives `useAutoLoadAllPages`. */
export function groupingNeedsEveryPage(levels: readonly string[]): boolean {
  return levels.some(k => isClientGroupLevel(k) || EVERY_PAGE_GROUP_KEYS.includes(k));
}

/**
 * Manual steps (0) before auto steps (1).
 * copied from mini-apps/action-view/grouping.ts:319-324 (the `tier` helper) —
 * the archived/closed → 2 branch is omitted: no Signals tactic label ever
 * carries those words (the Closed view is a separate preset).
 */
export function stepTypeTier(stepType: string | null | undefined): number {
  const l = (stepType ?? '').toLowerCase();
  if (l.includes('auto')) return 1;
  return 0;
}

interface GroupStats {
  /** Smallest numeric `priority` among the members; Infinity when none carries one (AV `minMemberPriority`). */
  minPriority: number;
  /** First row index in server order — the final tiebreak. */
  firstIndex: number;
  size: number;
  rawType: string | null | undefined;
  rawTactic: string | null | undefined;
}

/**
 * One rank per row (aligned with `rows`) for the level `groupBy[level]`,
 * per the table in the module doc. Lower rank = earlier group.
 */
export function rankerLevelRank<T extends RankerOrderableRow>(
  rows: readonly T[],
  groupBy: readonly string[],
  level: number,
  groupValue: (row: T, key: string) => unknown,
  canonical: CanonicalRanking,
): number[] {
  const key = groupBy[level];
  const typeIdx = groupBy.indexOf(TYPE_KEY);
  const inherits = typeIdx !== -1 && level > typeIdx;
  const sortKey = (g: GroupStats): number[] => {
    if (key === TYPE_KEY) return [g.minPriority, canonical.typeMinRank(g.rawType), -g.size, g.firstIndex];
    if (key === STEP_KEY) {
      const step = [stepTypeTier(g.rawTactic), stepTypeRank(g.rawTactic), g.firstIndex];
      return inherits ? [g.minPriority, ...step] : step;
    }
    return [g.minPriority, g.firstIndex];
  };

  const levels = groupBy.slice(0, level + 1);
  const tupleOf = rows.map(row => levels.map(k => groupValueOf(groupValue(row, k))).join(GROUP_PATH_SEP));
  const groups = new Map<string, GroupStats>();
  rows.forEach((row, i) => {
    let g = groups.get(tupleOf[i]);
    if (!g) {
      g = { minPriority: Infinity, firstIndex: i, size: 0, rawType: row.signal_source_type, rawTactic: row.signal_tactic };
      groups.set(tupleOf[i], g);
    }
    g.size += 1;
    if (typeof row.priority === 'number' && row.priority < g.minPriority) g.minPriority = row.priority;
  });

  const keyed = [...groups.entries()].map(([tuple, g]) => ({ tuple, k: sortKey(g) }));
  keyed.sort((a, b) => {
    for (let j = 0; j < a.k.length; j++) {
      // Components may be Infinity — compare, never subtract.
      if (a.k[j] !== b.k[j]) return a.k[j] < b.k[j] ? -1 : 1;
    }
    return 0;
  });
  const position = new Map(keyed.map(({ tuple }, i) => [tuple, i]));
  return tupleOf.map(tuple => position.get(tuple) ?? 0);
}

/**
 * The `serverLevelRank` option `arrangeSignalsClientSide` accepts — an entry
 * only for the levels the rule overrides (Type anywhere, Step anywhere,
 * every other SERVER key below a Type level; never `local_time` /
 * `business_hours`); `{}` when none. Each entry ranks the `rows` the
 * arranger hands it, never a captured array, so indices always align.
 */
export function buildRankerLevelOverrides<T extends RankerOrderableRow>(
  groupBy: readonly string[],
  groupValue: (row: T, key: string) => unknown,
  canonical: CanonicalRanking,
): Partial<Record<string, ServerLevelRankFn<T>>> {
  const typeIdx = groupBy.indexOf(TYPE_KEY);
  const overrides: Partial<Record<string, ServerLevelRankFn<T>>> = {};
  groupBy.forEach((key, level) => {
    if (isClientGroupLevel(key)) return;
    const overridden = isRankerOrderedLevel(key) || (typeIdx !== -1 && level > typeIdx);
    if (!overridden) return;
    overrides[key] = ({ rows }) => rankerLevelRank(rows, groupBy, level, groupValue, canonical);
  });
  return overrides;
}
